import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";

/**
 * Heatmap de volume: dia da semana × hora.
 *
 * Codificação sequencial de uma hue só, claro→escuro — magnitude contínua.
 * Nada de arco-íris aqui: matiz variando por valor faz o leitor procurar
 * categoria onde só existe intensidade.
 */

// Rampa azul sequencial (steps 100→700). O passo mais claro fica perto da
// superfície de propósito: "quase zero" deve recuar.
const RAMP_LIGHT = [
  "#cde2fb",
  "#9ec5f4",
  "#6da7ec",
  "#3987e5",
  "#256abf",
  "#184f95",
  "#0d366b",
];

// Dark mode é escolhido, não é o claro invertido: no fundo escuro a rampa
// precisa CRESCER em luminosidade para "mais" continuar lendo como "mais".
const RAMP_DARK = [
  "#123056",
  "#16406f",
  "#1c5cab",
  "#2a78d6",
  "#5598e7",
  "#86b6ef",
  "#b7d3f6",
];

const WEEKDAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

const useStyles = makeStyles((theme) => ({
  root: { width: "100%", overflowX: "auto" },
  grid: {
    display: "grid",
    gridTemplateColumns: "auto repeat(24, minmax(18px, 1fr))",
    gap: 2,
    minWidth: 620,
  },
  corner: {},
  hourLabel: {
    fontSize: 10,
    textAlign: "center",
    color: theme.palette.text.hint,
    lineHeight: "16px",
  },
  dayLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    paddingRight: 8,
    display: "flex",
    alignItems: "center",
    whiteSpace: "nowrap",
  },
  cell: {
    aspectRatio: "1 / 1",
    minHeight: 18,
    borderRadius: 3,
    cursor: "default",
    transition: "outline-color 120ms",
    outline: "2px solid transparent",
    outlineOffset: -1,
  },
  cellActive: {
    outlineColor: theme.palette.text.primary,
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
  },
  legendSwatch: { width: 16, height: 12, borderRadius: 2 },
  footnote: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

const TicketHeatmap = ({ cells, max, total, busiest, isDark }) => {
  const classes = useStyles();
  const [active, setActive] = useState(null);
  const ramp = isDark ? RAMP_DARK : RAMP_LIGHT;

  const byKey = new Map();
  cells.forEach((c) => byKey.set(`${c.weekday}-${c.hour}`, c.total));

  const emptyColor = isDark ? "rgba(148,163,184,0.08)" : "#F1F5F9";

  const colorFor = (value) => {
    if (!value) return emptyColor;
    if (!max) return emptyColor;
    // Escala em raiz: sem isso um pico isolado achata o resto do grid inteiro
    // e o padrão semanal — que é o ponto do gráfico — some.
    const ratio = Math.sqrt(value / max);
    const idx = Math.min(ramp.length - 1, Math.floor(ratio * ramp.length));
    return ramp[idx];
  };

  return (
    <div className={classes.root}>
      <div className={classes.grid}>
        <div className={classes.corner} />
        {Array.from({ length: 24 }).map((_, hour) => (
          <div key={`h-${hour}`} className={classes.hourLabel}>
            {hour % 3 === 0 ? hour : ""}
          </div>
        ))}

        {WEEKDAYS.map((label, weekday) => (
          <React.Fragment key={label}>
            <div className={classes.dayLabel}>{label}</div>
            {Array.from({ length: 24 }).map((_, hour) => {
              const value = byKey.get(`${weekday}-${hour}`) || 0;
              const key = `${weekday}-${hour}`;
              return (
                <Tooltip
                  key={key}
                  title={`${label}, ${String(hour).padStart(2, "0")}h — ${value} ${
                    value === 1 ? "ticket" : "tickets"
                  }`}
                >
                  <div
                    className={`${classes.cell} ${
                      active === key ? classes.cellActive : ""
                    }`}
                    style={{ backgroundColor: colorFor(value) }}
                    onMouseEnter={() => setActive(key)}
                    onMouseLeave={() => setActive(null)}
                  />
                </Tooltip>
              );
            })}
          </React.Fragment>
        ))}
      </div>

      <div className={classes.legend}>
        <Typography variant="caption">Menos</Typography>
        <div
          className={classes.legendSwatch}
          style={{ backgroundColor: emptyColor }}
        />
        {ramp.map((color) => (
          <div
            key={color}
            className={classes.legendSwatch}
            style={{ backgroundColor: color }}
          />
        ))}
        <Typography variant="caption">Mais</Typography>
        <Typography variant="caption" style={{ marginLeft: 8 }}>
          (pico: {max})
        </Typography>
      </div>

      <Typography variant="body2" className={classes.footnote}>
        {total} tickets no período.
        {busiest
          ? ` Horário mais movimentado: ${WEEKDAYS[busiest.weekday]} às ${String(
              busiest.hour
            ).padStart(2, "0")}h (${busiest.total}).`
          : ""}
      </Typography>
    </div>
  );
};

TicketHeatmap.propTypes = {
  cells: PropTypes.arrayOf(
    PropTypes.shape({
      weekday: PropTypes.number,
      hour: PropTypes.number,
      total: PropTypes.number,
    })
  ),
  max: PropTypes.number,
  total: PropTypes.number,
  busiest: PropTypes.shape({
    weekday: PropTypes.number,
    hour: PropTypes.number,
    total: PropTypes.number,
  }),
  isDark: PropTypes.bool,
};

TicketHeatmap.defaultProps = {
  cells: [],
  max: 0,
  total: 0,
  busiest: null,
  isDark: false,
};

export default TicketHeatmap;
